import { createHttpClient, HttpClientError } from '@tmobile/http-client_nodejs';
import forge from 'node-forge';
import poptoken_builder from '../popToken/poptoken-builder-node';

const { VITE_CLIENTID, VITE_GUEST_TOKEN_URL } = typeof import.meta !== 'undefined' ? import.meta.env : process.env;

// Helper function to validate URLs (SSRF protection)
const validateUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      console.error('Invalid protocol:', parsedUrl.protocol);
      return false;
    }
    if (isInternalIp(parsedUrl.hostname)) {
      console.error('Internal IP not allowed:', parsedUrl.hostname);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Invalid URL format:', error);
    return false;
  }
};

// Helper function to check if the IP is internal
const isInternalIp = (hostname) => {
  const privateIpRegex = /^(localhost|127\.0\.0\.1|::1|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|fc00::|fe80::)/;
  return privateIpRegex.test(hostname);
};

const createPopToken = (method, url, authorization, body, privateKey) => {
  const ehtsKeyValueMap = new Map();
  ehtsKeyValueMap.set('http-method', method.toUpperCase());
  const privateKeyPemStr = '' + privateKey;
  try {
    return poptoken_builder.buildPopToken(ehtsKeyValueMap, privateKeyPemStr);
  } catch (error) {
    console.log('Error:', error);
  }
};

/**
 * Creates an authenticated HTTP client using @tmobile/http-client_nodejs.
 * Validates baseUrl (SSRF protection), acquires a fresh id_token via VITE_GUEST_TOKEN_URL,
 * and returns a configured HTTP client instance.
 * @param {string} baseUrl - Base URL for the HTTP client
 * @param {string} method - HTTP method for the request (used to generate POP token)
 * @param {string} path - Request path appended to baseUrl (used to generate POP token)
 * @returns {Object} - HTTP client instance with authorization headers
 */
export const createAuthenticatedClient = async (baseUrl, method = 'get', path = '') => {
  if (!validateUrl(baseUrl)) {
    throw new Error('Invalid URL: SSRF protection - URL not allowed');
  }

  // Generate RSA key pair for PoP token signing
  const keypair = forge.pki.rsa.generateKeyPair({ bits: 2048, e: 0x10001 });
  const publicKey = forge.pki.publicKeyToPem(keypair.publicKey);
  const privateKey = forge.pki.privateKeyToPem(keypair.privateKey);

  // Fetch a fresh id_token from the guest token endpoint
  const guestTokenResponse = await fetch(VITE_GUEST_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Authorization': createPopToken('post', VITE_GUEST_TOKEN_URL, '', null, privateKey),
    },
    body: JSON.stringify({ client_id: VITE_CLIENTID, cnf: publicKey }),
  });

  if (!guestTokenResponse.ok) {
    throw new Error(`Guest token request failed: ${guestTokenResponse.status}`);
  }

  const { id_token } = await guestTokenResponse.json();
  if (!id_token) {
    throw new Error('Failed to fetch fresh id_token from guestToken endpoint');
  }

  const fullUrl = `${baseUrl}${path}`;

  return createHttpClient({
    baseUrl,
    headers: {
      Authorization: `Bearer ${id_token}`,
      'X-Auth-Originator': id_token,
      'X-Authorization': createPopToken(method, fullUrl, '', null, privateKey),
    },
  });
};

export { HttpClientError };